import React from "react";
import CaseStudiesCard from "../CaseStudiesCard";
import Links from "../../../Links";
import Heading from "../../../Heading";
import AirwallexImage from "../../../../img/airwallex-guidelines.png";
import GuruGuidesDefaultImage from "../../../../img/guru-guides-default.png";
import GuruGuidesCustomImage from "../../../../img/guru-guides-custom.png";
import GuruGuidesCustomWebsite from "../../../../img/hero-guides-browser.png";
import Box from "../../../Box";
import Rule from "../../../Rule";
import Image from "../../../Image";
import BlogLayout from "../BlogLayout";
import Stack from "../../../Stack";

const Guidelines = (
  <CaseStudiesCard id="guidelines">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Making UX guidelines accessible in Figma to boost consistency and collaboration.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout>
      <Stack spacing="var(--spacing-xx-large)">
        <Box>
          <Heading variant="larger">Guidelines at Your Fingertips</Heading>
          <p className="intro">
            To maintain consistency across products at Airwallex during the design exploration phases, we closely
            integrated UX Guidelines within Figma, ensuring they were always accessible as designers prototyped new
            experiences. This approach helped designers utilise common patterns across products and avoid introducing
            multiple variations of similar approaches, aligning design intent with development execution from the
            outset.
          </p>
          <p className="intro">
            <Links
              href="https://medium.com/@trilfordm/how-to-create-high-quality-guidelines-for-your-design-system-library-6127b482c1e8"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </Links>{" "}
            about how I created high-quality guidelines.
          </p>
          <Image
            dark
            alt="Example of guidelines within Figma"
            caption="Example of guidelines within Figma"
            src={AirwallexImage}
          />
        </Box>

        <Box>
          <Heading variant="large">What I learnt</Heading>
          <p>
            While embedding guidelines directly in Figma made them highly accessible, it also introduced a significant
            performance challenge. Opening component pages would often trigger the memory warning, with noticeable lag
            and, at times, instability. This primarily affected the Design System team maintaining the content, but we
            quickly pivoted to avoid impacting other teams’ productivity when accessing those pages.
          </p>
          <p>
            In response, we reduced the amount of visible guidance on component pages, replacing it with direct links to
            the relevant documentation in our central guideline workspace.
          </p>
          <p>
            Moving forward, I was interested in exploring ways to make guidelines easier to access within Figma, even if
            the tool itself didn’t yet offer the features needed to support that kind of solution. That’s where the idea
            of a plugin to deliver guidelines directly in Figma began to take shape.
          </p>
        </Box>
        <Stack spacing="var(--spacing-large)">
          <Box>
            <Heading variant="large">Introducing Guru Guides</Heading>

            <p>
              <Links
                href="https://www.figma.com/community/plugin/1471341082690554711/guru-guides"
                target="_blank"
                rel="noopener noreferrer"
              >
                Install Plugin
              </Links>{" "}
              •{" "}
              <Links href="https://guides.gurusuite.xyz" target="_blank" rel="noopener noreferrer">
                Website
              </Links>{" "}
            </p>
            <p>
              During my sabbatical in 2024, I began exploring more modular, on-demand approaches to delivering
              documentation. This led to the creation of Guru Guides, a Figma plugin that provides contextual access to
              Design System guidance without the performance overhead of embedding content directly into files. It
              allows designers to reference documentation as needed, without disrupting their workflow or slowing down
              the Figma experience.
            </p>
            <p>
              Guru Guides is a free tool to help teams begin documenting their design system guidelines. The main goal
              is to increase engagement with guidelines during the design process, making it easier for designers to
              maintain consistency across products as teams grow. By bringing guidelines directly into Figma or via the
              web, we reduce context switching and help designers stay focused in their tool of choice, with guidelines
              at their fingertips whenever summoned.
            </p>
            <p>
              Read the article: {""}
              <Links
                href="https://medium.com/@trilfordm/guidelines-at-your-fingertips-848bdaeee0ee"
                target="_blank"
                rel="noopener noreferrer"
              >
                Guidelines at Your Fingertips
              </Links>
            </p>
            <Image
              dark
              alt="Guru Guides: Figma Plugin"
              caption="Guru Guides: Figma Plugin"
              src={GuruGuidesDefaultImage}
            />
          </Box>
          <Box>
            <Heading variant="medium">Customising Guru Guides</Heading>
            <p>
              The plugin is highly customisable, allowing other teams to surface their own on-demand guidelines within
              the tools they already use. Guru Guides was designed with findability and scalability in mind. By
              integrating with Contentful, rather than embedding large volumes of content directly in Figma, it avoids
              the memory issues and crashes that can come from overloaded files. This approach keeps performance smooth,
              while still ensuring designers have access to the guidance they need, when they need it.
            </p>
            <Image
              dark
              alt="Feelix Org Guidelines (Figma Plugin)"
              caption="Feelix Org Guidelines (Figma Plugin)"
              src={GuruGuidesCustomImage}
            />
          </Box>
          <Box>
            <Heading variant="medium">Guru Guides: Website</Heading>
            <p>
              The tool is also available via the web, ensuring guidelines are accessible to all team members, regardless
              of their role or access to design tools. This inclusivity helps foster a shared understanding of design
              principles across the entire team, from designers to product managers and developers.
            </p>

            <Image
              dark
              alt="Muibook Org Guidelines (Website)"
              caption="Muibook Org Guidelines (Website)"
              src={GuruGuidesCustomWebsite}
            />
          </Box>
        </Stack>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Guidelines;
